import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { RESTAURANT, IMAGES } from "../data/restaurant";

const values = [
  {
    title: "Spices, ground fresh",
    body: "We blend our own masalas weekly — coriander, cumin, cardamom, fenugreek — toasted before they ever hit a pan.",
  },
  {
    title: "Tandoor + pizza oven",
    body: "Two ovens. One philosophy. High heat, blistered crusts, and a charred soul in every bite.",
  },
  {
    title: "Local where we can",
    body: "Alberta produce, Canadian dairy, and ingredients sourced from suppliers we know by name.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-[#FAF8F5]">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-12">
        <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-4">
          Our Story
        </div>
        <h1 className="font-display text-5xl md:text-7xl font-extrabold leading-[1.02] text-[#0E0E1A] max-w-4xl">
          A fusion built on<br />
          <span className="font-accent italic font-medium text-[#C9853E]">tradition, fire, and family.</span>
        </h1>
      </section>

      {/* Image strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <div className="col-span-7 aspect-[4/3] rounded-3xl overflow-hidden">
            <img src={IMAGES.tandoor} alt="Inside the kitchen" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-5 aspect-[4/3] rounded-3xl overflow-hidden mt-12 md:mt-20">
            <img src={IMAGES.spices} alt="Spices" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Long copy */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <p className="font-accent italic text-2xl md:text-3xl text-[#0E0E1A] leading-snug mb-10">
          "We started Rivaaz to share the food we grew up loving — the curries that filled our
          grandmothers' kitchens — with the city we now call home."
        </p>
        <div className="space-y-6 text-lg leading-relaxed text-[#57534E]">
          <p>
            <span className="font-display font-bold text-[#0E0E1A]">Rivaaz</span> means tradition.
            For us, it's the tradition of doing the slow things right — toasting whole spices,
            kneading dough by hand, and stirring a curry until it tells you it's ready. But we're
            also a Canadian family in Lethbridge, raised on weekend pizza nights and big shared
            tables. So we asked the obvious question: what if the two could meet?
          </p>
          <p>
            The answer turned out to be a butter chicken pizza so good it shocked us. And then a
            tandoori paneer pizza. And a spicy lamb keema pizza. And, somewhere along the way, an
            entire menu of fusion creations sitting next to the classic dals and biryanis we
            refuse to compromise on.
          </p>
          <p>
            Today, Rivaaz is a small kitchen run by a tight team that genuinely cares whether you
            love your meal. Every pie is hand-stretched. Every curry is built from a base, not a
            jar. Every naan comes out of the tandoor minutes before it lands on your table.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0E0E1A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            {values.map((v, i) => (
              <div key={i} data-testid={`value-${i}`}>
                <div className="font-accent italic text-5xl text-[#E5B07D]">0{i + 1}</div>
                <h3 className="font-display text-2xl font-bold mt-4">{v.title}</h3>
                <p className="text-white/75 mt-3 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="aspect-[4/3] rounded-3xl overflow-hidden">
            <img src={IMAGES.butterChicken} alt="Butter chicken" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A]">
              Come hungry. Leave happy.
            </h2>
            <p className="text-[#57534E] mt-5 text-lg leading-relaxed">
              Whether it's a quick takeout pizza or a long dinner with the whole family, we'd love
              to feed you tonight.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to="/menu"
                data-testid="about-order-btn"
                className="inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-7 py-4 font-bold"
              >
                Start your order <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${RESTAURANT.phoneRaw}`}
                data-testid="about-call-btn"
                className="inline-flex items-center gap-2 bg-white border-2 border-[#0E0E1A] text-[#0E0E1A] hover:bg-[#0E0E1A] hover:text-white rounded-full px-7 py-4 font-bold transition-all"
              >
                <Phone className="w-4 h-4" /> Call {RESTAURANT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
